import {useState} from 'react'
import {AlertIcon, StopIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash} from '@primer/react'
import {ConflictingFilesList} from '@github-ui/mergebox/components/sections/conflicts/ConflictingFilesList'
import {MergeBoxExpandable} from '@github-ui/mergebox/components/sections/common/MergeBoxExpandable'
import {ResolveConflictsButton} from '@github-ui/mergebox/components/sections/conflicts/ResolveConflictsButton'
import {
  CONFLICT_SECTION_STATUSES,
  ConflictResolutionSubtitle,
} from '@github-ui/mergebox/components/sections/conflicts/conflicts-section-presentation'
import {ForYouItem} from '../shared/ForYouItem'
import {type MergeabilityData, useMergeabilityData} from './use-mergeability-data'
import {ForYouLeadingIcon} from '../shared/ForYouLeadingIcon'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import styles from './MergeConflicts.module.css'

type MergeConflictsData = MergeabilityData & {
  conflictsSection: MergeabilityData['conflictsSection'] & {
    conflictsCondition: NonNullable<MergeabilityData['conflictsSection']['conflictsCondition']>
  }
}

export function shouldShowMergeConflicts(data: ReturnType<typeof useMergeabilityData>): data is MergeConflictsData {
  const conflictsSection = data?.conflictsSection
  const isConflictState =
    conflictsSection?.sectionStatus === 'HAS_CONFLICTS' || conflictsSection?.sectionStatus === 'HAS_REBASE_CONFLICTS'

  return (
    !!data &&
    !data.isClosedOrMerged &&
    !!conflictsSection?.shouldRender &&
    isConflictState &&
    !!conflictsSection.conflictsCondition
  )
}

// Renders when the branch has merge conflicts that must be resolved
export function MergeConflicts({variant}: {variant?: 'default' | 'embedded'}) {
  const data = useMergeabilityData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  if (!shouldShowMergeConflicts(data)) return null

  const {mergeBoxPullRequest, conflictsSection, canUserPushToBase} = data
  const {sectionStatus, conflictsCondition} = conflictsSection

  const sectionPresentation = CONFLICT_SECTION_STATUSES[sectionStatus]
  const conflictsWebEditorPath = `${mergeBoxPullRequest.resourcePath}/conflicts`
  const showAgentOption = canUserPushToBase && !!mergeBoxPullRequest.viewerCanUseCopilotCodingAgent
  const sendConflictAction = (actionName: 'edit_conflicts_on_web' | 'fix_conflicts_with_copilot') =>
    sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
      item_name: 'merge-conflicts',
      action_name: actionName,
    })

  // only list files if user can resolve conflict or user can push to head (aka has sufficient access)
  const shouldListConflictingFiles =
    sectionStatus === 'HAS_CONFLICTS' &&
    (conflictsCondition.webEditorConflictResolution?.viewerCanResolve ||
      conflictsCondition.webEditorConflictResolution?.viewerCannotResolve?.reason !== 'INSUFFICIENT_ACCESS')

  const conflicts = conflictsCondition.conflicts ?? []

  return (
    <ForYouItem
      leadingVisual={<ForYouLeadingIcon icon={AlertIcon} variant="danger" />}
      title={sectionPresentation.heading}
      itemName="merge-conflicts"
      padding="none"
      variant={variant}
      description={
        sectionStatus === 'HAS_CONFLICTS' ? (
          <ConflictResolutionSubtitle
            conflictsWebEditorPath={conflictsWebEditorPath}
            webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
            onClick={() => sendConflictAction('edit_conflicts_on_web')}
          />
        ) : undefined
      }
      actionSlot={
        <ResolveConflictsButton
          conflictsWebEditorPath={conflictsWebEditorPath}
          conflictsState={sectionStatus}
          onError={setErrorMessage}
          onActionSelect={sendConflictAction}
          showAgentOption={showAgentOption}
          webEditorConflictResolution={conflictsCondition.webEditorConflictResolution}
          size="small"
        />
      }
    >
      {shouldListConflictingFiles && (
        <MergeBoxExpandable
          isExpanded
          wrapperClassName={styles.ConflictingFilesWrapper}
          contentClassName={styles.ConflictingFilesContent}
        >
          <ConflictingFilesList conflicts={conflicts} />
        </MergeBoxExpandable>
      )}
      {errorMessage && (
        <Flash className="tmp-m-3" variant="danger" role="alert">
          <StopIcon className="mr-2" />
          {errorMessage}
        </Flash>
      )}
    </ForYouItem>
  )
}
